import { Updater } from '@headless-tree/core';
import { useCallback, useMemo, useRef } from 'react';
import { ID } from 'shared/types/id';

import { rootId } from './constants';
import { InternalTreeItem, TreeItemID } from './types';

export const useExpandedItems = (
  items: Record<TreeItemID, InternalTreeItem>,
  collapsedItems: ID[] = [],
  onCollapse?: (ids: ID[]) => void,
) => {
  const itemValues = useMemo(() => Object.values(items), [items]);

  // OPTIMIZATION 1: Create a Lookup Map (Index)
  // This turns finding items by sourceId from O(N) to O(1)
  const itemsBySourceId = useMemo(() => {
    const map = new Map<ID, InternalTreeItem[]>();
    for (const item of itemValues) {
      const group = map.get(item.sourceId);
      if (group) {
        group.push(item);
      } else {
        map.set(item.sourceId, [item]);
      }
    }
    return map;
  }, [itemValues]);

  // collapsedSections must hold only foldable section ids — never leaf rows.
  // Writing tasks / new-task rows persists every task id, which on All Tasks can
  // exceed Firestore's document-size limit and silently drop the write, so the
  // section never stays collapsed.
  const collapsibleSourceIds = useMemo(() => {
    const ids = new Set<ID>();
    for (const item of itemValues) {
      if (item.sourceId !== rootId && !!item.children?.length) {
        ids.add(item.sourceId);
      }
    }
    return ids;
  }, [itemValues]);

  // OPTIMIZATION 2: Use Sets for faster lookups
  // Converting collapsedItems to path IDs is now O(N) instead of O(N * M)
  const collapsedPathIdsSet = useMemo(() => {
    const pathIds = new Set<TreeItemID>();
    for (const sourceId of collapsedItems) {
      const matchingItems = itemsBySourceId.get(sourceId);
      if (matchingItems) {
        for (const item of matchingItems) {
          pathIds.add(item.id);
        }
      }
    }
    return pathIds;
  }, [collapsedItems, itemsBySourceId]);

  // Preserve the previous array reference when the contents are unchanged,
  // so downstream consumers (headless-tree, deps arrays, memoized children)
  // don't re-run on every itemValues / collapsedPathIdsSet change.
  const expandedItemsRef = useRef<TreeItemID[]>([]);
  const expandedItems = useMemo(() => {
    const nextExpandedItems = itemValues
      .filter(
        // OPTIMIZATION 3: .has() is O(1), whereas .includes() is O(N)
        (item) => !!item.children?.length && !collapsedPathIdsSet.has(item.id),
      )
      .map((item) => item.id);

    const prev = expandedItemsRef.current;
    const isSame =
      prev.length === nextExpandedItems.length &&
      prev.every((id, i) => id === nextExpandedItems[i]);

    if (isSame) {
      return prev;
    }

    expandedItemsRef.current = nextExpandedItems;
    return nextExpandedItems;
  }, [itemValues, collapsedPathIdsSet]);

  const setExpandedItems = useCallback(
    (newIds: Updater<TreeItemID[]>) => {
      if (!onCollapse) {
        return;
      }

      const _newIds = Array.isArray(newIds) ? newIds : newIds(expandedItems);

      // Create a Set of the NEW expanded source IDs for O(1) lookup
      const newExpandedSourceIds = new Set<ID>();
      for (const pathId of _newIds) {
        const item = items[pathId];
        if (item) {
          newExpandedSourceIds.add(item.sourceId);
        }
      }

      // Collapsed = every foldable section that is not in the expanded set.
      const nextCollapsedIds = Array.from(collapsibleSourceIds).filter(
        (id) => !newExpandedSourceIds.has(id),
      );

      onCollapse(nextCollapsedIds);
    },
    [expandedItems, items, onCollapse, collapsibleSourceIds],
  );

  return { expandedItems, setExpandedItems };
};
